import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { DashboardStats, Settings, Theme, WSStatus, TruncateMetricsResult } from '../types';
import { apiClient } from '../api';

// Dual state storage: one for global (unfiltered) view, one for filtered view
interface DashboardStateStore {
  stats: DashboardStats | null;
  lastSyncTimestamp: number | null;
  currentBucketSeconds: number | null;
}

interface AppState {
  // UI State
  theme: Theme;
  sidebarCollapsed: boolean;
  
  // Dual Data State - separate stores for filtered and unfiltered views
  globalState: DashboardStateStore;      // For main dashboard (no filters)
  filteredState: DashboardStateStore;    // For channel/gateway/identity filtered views
  
  // Active view selector
  activeView: 'global' | 'filtered';
  
  settings: Settings | null;
  
  // Current filter configuration
  dashboardBucketOverride: number | null; // User override from dashboard dropdown
  dashboardFilters: {
    channelId?: string;
    gatewayId?: string;
    identityDid?: string;
    pipe_id?: string;
  } | null; // Global filters applied to all dashboard queries
  
  // WebSocket State
  wsStatus: WSStatus;
  wsConnection: WebSocket | null;
  
  // Loading States
  isLoading: {
    dashboard: boolean;
    settings: boolean;
  };
  
  // Error State
  error: string | null;
}

type AppAction =
  | { type: 'SET_THEME'; payload: Theme }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_DASHBOARD_STATS'; payload: { stats: DashboardStats; view: 'global' | 'filtered' } }
  | { type: 'APPLY_DASHBOARD_DELTA'; payload: { delta: any; view: 'global' | 'filtered' } }
  | { type: 'SET_BUCKET_SECONDS'; payload: { bucket: number | null; view: 'global' | 'filtered' } }
  | { type: 'SET_DASHBOARD_BUCKET_OVERRIDE'; payload: number | null }
  | { type: 'SET_DASHBOARD_FILTERS'; payload: { channelId?: string; gatewayId?: string; identityDid?: string; pipe_id?: string } | null }
  | { type: 'SET_ACTIVE_VIEW'; payload: 'global' | 'filtered' }
  | { type: 'SET_SETTINGS'; payload: Settings }
  | { type: 'SET_LOADING'; payload: { key: keyof AppState['isLoading']; value: boolean } }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_WS_STATUS'; payload: WSStatus }
  | { type: 'SET_WS_CONNECTION'; payload: WebSocket | null }
  | { type: 'UPDATE_STATS_FROM_WS'; payload: { metrics?: Partial<DashboardStats['metrics']> } }
  | { type: 'UPDATE_ROUTE_HEALTH'; payload: { configId: string; health: string } }
  | { type: 'REMOVE_CHANNEL'; payload: { configId: string } }
  | { type: 'ADD_CHANNEL'; payload: { channel: any } }
  | { type: 'UPDATE_CHANNEL'; payload: { channel: any } };

const initialDashboardStore: DashboardStateStore = {
  stats: null,
  lastSyncTimestamp: null,
  currentBucketSeconds: null,
};

const initialState: AppState = {
  theme: 'light',
  sidebarCollapsed: false,
  globalState: { ...initialDashboardStore },
  filteredState: { ...initialDashboardStore },
  activeView: 'global',
  settings: null,
  dashboardBucketOverride: null,
  dashboardFilters: null,

  wsStatus: 'CONNECTING',
  wsConnection: null,
  isLoading: {
    dashboard: false,
    settings: false,
  },
  error: null,
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_THEME':
      return { ...state, theme: action.payload };
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarCollapsed: !state.sidebarCollapsed };
      
    case 'SET_ACTIVE_VIEW':
      return { ...state, activeView: action.payload };
      
    case 'SET_DASHBOARD_STATS':
      const { stats, view } = action.payload;
      const targetStore = view === 'global' ? 'globalState' : 'filteredState';
      return {
        ...state,
        [targetStore]: {
          ...state[targetStore],
          stats,
          lastSyncTimestamp: Math.floor(Date.now() / 1000),
        }
      };
      
    case 'SET_BUCKET_SECONDS':
      const bucketTargetStore = action.payload.view === 'global' ? 'globalState' : 'filteredState';
      return {
        ...state,
        [bucketTargetStore]: {
          ...state[bucketTargetStore],
          currentBucketSeconds: action.payload.bucket,
        }
      };
      
    case 'SET_DASHBOARD_BUCKET_OVERRIDE':
      // Reset both stores' timestamps when bucket override changes to force full refresh
      return {
        ...state,
        dashboardBucketOverride: action.payload,
        globalState: {
          ...state.globalState,
          lastSyncTimestamp: null,
        },
        filteredState: {
          ...state.filteredState,
          lastSyncTimestamp: null,
        }
      };
      
    case 'APPLY_DASHBOARD_DELTA':
      const deltaView = action.payload.view;
      const deltaTargetStore = deltaView === 'global' ? 'globalState' : 'filteredState';
      const currentStore = state[deltaTargetStore];
      
      if (!currentStore.stats) {
        return state; // Can't apply delta without initial state
      }
      
      const delta = action.payload.delta;
      const updated = { ...currentStore.stats };
      
      // Apply metrics updates
      if (delta.changes?.metrics) {
        const metricsUpdate = delta.changes.metrics;
        
        // Check if bucket interval changed - if so, force full refresh
        if (currentStore.currentBucketSeconds !== null && 
            metricsUpdate.bucket_seconds !== currentStore.currentBucketSeconds) {
          return {
            ...state,
            [deltaTargetStore]: {
              ...currentStore,
              lastSyncTimestamp: null,
              currentBucketSeconds: null,
            }
          };
        }
        
        // Merge new time series buckets (deduplicate by timestamp)
        let updatedTimeSeries = updated.metrics.time_series || [];
        if (metricsUpdate.new_time_series_points?.length > 0) {
          const existingMap = new Map(updatedTimeSeries.map((point: any) => [point.timestamp, point]));
          
          metricsUpdate.new_time_series_points.forEach((point: any) => {
            existingMap.set(point.timestamp, point);
          });
          
          updatedTimeSeries = Array.from(existingMap.values()).sort((a: any, b: any) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
        }
        
        // Merge new latency series points (deduplicate by timestamp)
        let updatedLatencySeries = updated.metrics.latency_time_series || [];
        if (metricsUpdate.new_latency_points?.length > 0) {
          const existingLatencyMap = new Map(updatedLatencySeries.map((point: any) => [point.timestamp, point]));
          metricsUpdate.new_latency_points.forEach((point: any) => {
            existingLatencyMap.set(point.timestamp, point);
          });
          updatedLatencySeries = Array.from(existingLatencyMap.values()).sort((a: any, b: any) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
        }
        
        // Update metrics - preserve existing stats when filters are active (backend returns empty arrays)
        updated.metrics = {
          ...updated.metrics,
          time_series: updatedTimeSeries,
          latency_time_series: updatedLatencySeries,
          total_connections: metricsUpdate.total_connections !== undefined ? metricsUpdate.total_connections : updated.metrics.total_connections,
          avg_latency: metricsUpdate.avg_latency !== undefined ? metricsUpdate.avg_latency : updated.metrics.avg_latency,
          avg_request_latency: metricsUpdate.avg_request_latency !== undefined ? metricsUpdate.avg_request_latency : updated.metrics.avg_request_latency,
          avg_response_latency: metricsUpdate.avg_response_latency !== undefined ? metricsUpdate.avg_response_latency : updated.metrics.avg_response_latency,
          avg_pipe_gateway_processing_ms: metricsUpdate.avg_pipe_gateway_processing_ms !== undefined ? metricsUpdate.avg_pipe_gateway_processing_ms : updated.metrics.avg_pipe_gateway_processing_ms,
          avg_pipe_target_processing_ms: metricsUpdate.avg_pipe_target_processing_ms !== undefined ? metricsUpdate.avg_pipe_target_processing_ms : updated.metrics.avg_pipe_target_processing_ms,
          channel_stats: metricsUpdate.channel_stats?.length > 0 ? metricsUpdate.channel_stats : updated.metrics.channel_stats,
          identity_channel_stats: metricsUpdate.identity_channel_stats?.length > 0 ? metricsUpdate.identity_channel_stats : updated.metrics.identity_channel_stats,
          pipe_stats: metricsUpdate.pipe_stats?.length >= 0 ? metricsUpdate.pipe_stats : updated.metrics.pipe_stats,
        };
      }
      
      // Apply log updates
      if (delta.changes?.logs?.new_entries?.length > 0) {
        updated.proxy_info = {
          ...updated.proxy_info,
          log_entries: [
            ...updated.proxy_info.log_entries,
            ...delta.changes.logs.new_entries
          ].slice(-300) // Keep last 300 entries
        };
      }
      
      // Apply channel updates
      if (delta.changes?.channels) {
        const channelMap = new Map(updated.channels.map((ch: any) => [ch.config_id, ch]));
        
        if (delta.changes.channels.added?.length > 0) {
          delta.changes.channels.added.forEach((channel: any) => {
            channelMap.set(channel.config_id, channel);
          });
        }
        
        if (delta.changes.channels.updated?.length > 0) {
          delta.changes.channels.updated.forEach((channel: any) => {
            channelMap.set(channel.config_id, channel);
          });
        }
        
        if (delta.changes.channels.removed?.length > 0) {
          delta.changes.channels.removed.forEach((id: string) => {
            channelMap.delete(id);
          });
        }
        
        updated.channels = Array.from(channelMap.values());
      }
      
      // Apply identity updates
      if (delta.changes?.identities) {
        const identityMap = new Map(updated.identities.map((id: any) => [id.did, id]));
        
        if (delta.changes.identities.added?.length > 0) {
          delta.changes.identities.added.forEach((identity: any) => {
            identityMap.set(identity.did, identity);
          });
        }
        
        if (delta.changes.identities.updated?.length > 0) {
          delta.changes.identities.updated.forEach((identity: any) => {
            identityMap.set(identity.did, identity);
          });
        }
        
        updated.identities = Array.from(identityMap.values());
        if (delta.changes.identities.total_identities !== undefined) {
          updated.total_identities = delta.changes.identities.total_identities;
        }
      }
      
      // Apply task updates
      if (delta.changes?.tasks) {
        const existingTasks = updated.tasks?.tasks || [];
        const newTasks = delta.changes.tasks.tasks.tasks || [];
        
        const taskMap = new Map(existingTasks.map((t: any) => [t.task_id, t]));
        newTasks.forEach((task: any) => {
          taskMap.set(task.task_id, task);
        });
        
        const mergedTasks = Array.from(taskMap.values());
        
        updated.tasks = {
          summary: delta.changes.tasks.tasks.summary,
          tasks: mergedTasks,
          metrics: delta.changes.tasks.tasks.metrics
        };
        updated.connection_point_tasks = delta.changes.tasks.connection_point_tasks;
        updated.mcp_proxy_tasks = delta.changes.tasks.mcp_proxy_tasks;
        updated.pipe_tasks = delta.changes.tasks.pipe_tasks;
      }
      
      // Apply unread notification count
      if (delta.changes?.unread_count !== undefined) {
        updated.unread_count = delta.changes.unread_count;
      }
      
      return {
        ...state,
        [deltaTargetStore]: {
          ...currentStore,
          stats: updated,
          lastSyncTimestamp: delta.timestamp,
        }
      };
      
    case 'SET_SETTINGS':
      return { ...state, settings: action.payload };
    case 'SET_LOADING':
      return { 
        ...state, 
        isLoading: { ...state.isLoading, [action.payload.key]: action.payload.value } 
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_WS_STATUS':
      return { ...state, wsStatus: action.payload };
    case 'SET_WS_CONNECTION':
      return { ...state, wsConnection: action.payload };
      
    case 'UPDATE_STATS_FROM_WS':
      // Update the active view's stats
      const wsTargetStore = state.activeView === 'global' ? 'globalState' : 'filteredState';
      const wsCurrentStore = state[wsTargetStore];
      
      return {
        ...state,
        [wsTargetStore]: {
          ...wsCurrentStore,
          stats: wsCurrentStore.stats
            ? {
                ...wsCurrentStore.stats,
                ...action.payload,
                metrics: action.payload.metrics
                  ? { ...wsCurrentStore.stats.metrics, ...action.payload.metrics }
                  : wsCurrentStore.stats.metrics
              }
            : null
        }
      };
      
    case 'UPDATE_ROUTE_HEALTH':
      // Update channel health in both stores if they have data
      const updateChannelHealth = (store: DashboardStateStore): DashboardStateStore => {
        if (!store.stats) return store;
        return {
          ...store,
          stats: {
            ...store.stats,
            channels: store.stats.channels.map(channel =>
              channel.config_id === action.payload.configId
                ? { ...channel, last_status: action.payload.health }
                : channel
            )
          }
        };
      };
      
      return {
        ...state,
        globalState: updateChannelHealth(state.globalState),
        filteredState: updateChannelHealth(state.filteredState),
      };
      
    case 'REMOVE_CHANNEL':
      // Remove channel from both stores
      const removeChannelFromStore = (store: DashboardStateStore): DashboardStateStore => {
        if (!store.stats) return store;
        return {
          ...store,
          stats: {
            ...store.stats,
            channels: store.stats.channels.filter(channel =>
              channel.config_id !== action.payload.configId
            )
          }
        };
      };
      
      return {
        ...state,
        globalState: removeChannelFromStore(state.globalState),
        filteredState: removeChannelFromStore(state.filteredState),
      };
      
    case 'ADD_CHANNEL':
      // Add channel to both stores
      const addChannelToStore = (store: DashboardStateStore): DashboardStateStore => {
        if (!store.stats) return store;
        return {
          ...store,
          stats: {
            ...store.stats,
            channels: [...store.stats.channels, action.payload.channel]
          }
        };
      };
      
      return {
        ...state,
        globalState: addChannelToStore(state.globalState),
        filteredState: addChannelToStore(state.filteredState),
      };
      
    case 'UPDATE_CHANNEL':
      // Update channel in both stores
      const updateChannelInStore = (store: DashboardStateStore): DashboardStateStore => {
        if (!store.stats) return store;
        return {
          ...store,
          stats: {
            ...store.stats,
            channels: store.stats.channels.map(channel =>
              channel.config_id === action.payload.channel.config_id
                ? { ...channel, ...action.payload.channel }
                : channel
            )
          }
        };
      };
      
      return {
        ...state,
        globalState: updateChannelInStore(state.globalState),
        filteredState: updateChannelInStore(state.filteredState),
      };
      
    case 'SET_DASHBOARD_FILTERS':
      // Check if filters actually changed
      const filtersChanged = JSON.stringify(state.dashboardFilters) !== JSON.stringify(action.payload);
      
      // If filters changed and we're setting a new filter (not clearing), reset filtered state for fresh load
      const isSettingFilter = action.payload !== null && (
        action.payload.channelId !== undefined ||
        action.payload.gatewayId !== undefined ||
        action.payload.identityDid !== undefined ||
        action.payload.pipe_id !== undefined
      );
      
      const isClearingFilter = action.payload === null;
      
      if (filtersChanged && isSettingFilter) {

        return {
          ...state,
          dashboardFilters: action.payload,
          activeView: 'filtered', // Switch to filtered view
          filteredState: {
            stats: null,
            lastSyncTimestamp: null,
            currentBucketSeconds: null,
          }
        };
      }
      
      // If clearing filters, switch back to global view
      if (filtersChanged && isClearingFilter && state.activeView === 'filtered') {

        return {
          ...state,
          dashboardFilters: action.payload,
          activeView: 'global',
        };
      }
      
      return {
        ...state,
        dashboardFilters: action.payload,
      };
      
    default:
      return state;
  }
}

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  // Helper to get the currently active dashboard stats based on view
  getCurrentStats: () => DashboardStats | null;
  actions: {
    toggleTheme: () => void;
    toggleSidebar: () => void;
    loadDashboardStats: (force?: boolean) => Promise<void>;
    setDashboardFilters: (filters: { channelId?: string; gatewayId?: string; identityDid?: string; pipe_id?: string } | null) => void;
    setActiveView: (view: 'global' | 'filtered') => void;
    loadSettings: () => Promise<void>;
    updateSettings: (settings: Partial<Settings>) => Promise<void>;
    resetSettings: () => Promise<void>;
    truncateMetrics: () => Promise<TruncateMetricsResult>;
    reloadChannels: () => Promise<void>;
    removeChannel: (configId: string) => void;
    addChannel: (channel: any) => void;
    updateChannel: (channel: any) => void;
    setDashboardBucketOverride: (bucket: number | null) => void;
    connectWebSocket: () => void;
    disconnectWebSocket: () => void;
  };
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Initialize theme from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('dashboard-theme') as Theme || 'light';
    dispatch({ type: 'SET_THEME', payload: savedTheme });
    
    // Apply theme to body
    if (savedTheme === 'dark') {
      document.body.classList.add('dark-theme');
    }
  }, []);

  // Actions - defined early so they can be used in WebSocket callbacks
  const lastRefreshTimeRef = React.useRef<number>(0);
  
  // Separate refs for global and filtered state syncing
  const globalLastSyncTimestampRef = React.useRef<number | null>(null);
  const filteredLastSyncTimestampRef = React.useRef<number | null>(null);
  const globalCurrentBucketSecondsRef = React.useRef<number | null>(null);
  const filteredCurrentBucketSecondsRef = React.useRef<number | null>(null);
  
  const refreshIntervalRef = React.useRef<number>(5); // Default 5 seconds
  const dashboardBucketOverrideRef = React.useRef<number | null>(null);
  const settingsRef = React.useRef<Settings | null>(null);
  const dashboardFiltersRef = React.useRef<AppState['dashboardFilters']>(null);
  const activeViewRef = React.useRef<'global' | 'filtered'>('global');
  const scheduledRefreshTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  
  // Keep refs in sync with state
  React.useLayoutEffect(() => {
    activeViewRef.current = state.activeView;
  }, [state.activeView]);
  
  // Use useLayoutEffect for timestamp refs to ensure they're synced before other effects run
  React.useLayoutEffect(() => {
    // Sync global state refs
    globalLastSyncTimestampRef.current = state.globalState.lastSyncTimestamp;
    globalCurrentBucketSecondsRef.current = state.globalState.currentBucketSeconds;
  }, [state.globalState.lastSyncTimestamp, state.globalState.currentBucketSeconds]);
  
  React.useLayoutEffect(() => {
    // Sync filtered state refs (including null values!)
    filteredLastSyncTimestampRef.current = state.filteredState.lastSyncTimestamp;
    filteredCurrentBucketSecondsRef.current = state.filteredState.currentBucketSeconds;
  }, [state.filteredState.lastSyncTimestamp, state.filteredState.currentBucketSeconds]);
  
  React.useEffect(() => {
    dashboardBucketOverrideRef.current = state.dashboardBucketOverride;
  }, [state.dashboardBucketOverride]);
  
  React.useEffect(() => {
    settingsRef.current = state.settings;
  }, [state.settings]);
  
  // Keep refresh interval ref in sync with settings
  React.useEffect(() => {
    refreshIntervalRef.current = state.settings?.refresh_interval_seconds || 5;
  }, [state.settings?.refresh_interval_seconds]);
  
  const loadDashboardStats = React.useCallback(async (
    force: boolean = false
  ): Promise<void> => {
    // Get refresh interval from ref (to avoid stale closure)
    const refreshInterval = refreshIntervalRef.current * 1000;
    const now = Date.now();
    
    // Throttle: Skip if called too recently (unless forced)
    if (!force && now - lastRefreshTimeRef.current < refreshInterval) {
      // If we're being throttled, schedule a refresh for when the throttle expires
      // (unless one is already scheduled)
      if (scheduledRefreshTimeoutRef.current === null) {
        const timeUntilNextRefresh = refreshInterval - (now - lastRefreshTimeRef.current);
        scheduledRefreshTimeoutRef.current = setTimeout(() => {
          scheduledRefreshTimeoutRef.current = null;
          loadDashboardStats(false);
        }, timeUntilNextRefresh);
      }
      return;
    }
    
    // Clear any scheduled refresh since we're doing one now
    if (scheduledRefreshTimeoutRef.current !== null) {
      clearTimeout(scheduledRefreshTimeoutRef.current);
      scheduledRefreshTimeoutRef.current = null;
    }
    
    lastRefreshTimeRef.current = now;
    
    dispatch({ type: 'SET_LOADING', payload: { key: 'dashboard', value: true } });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      // Get current filters and active view from refs
      const currentFilters = dashboardFiltersRef.current;
      const currentView = activeViewRef.current;
      
      // Determine which state store to use based on filters
      const isFilteredView = currentFilters !== null && (
        currentFilters.channelId !== undefined ||
        currentFilters.gatewayId !== undefined ||
        currentFilters.identityDid !== undefined ||
        currentFilters.pipe_id !== undefined
      );
      const targetView: 'global' | 'filtered' = isFilteredView ? 'filtered' : 'global';
      
      // Get appropriate refs for this view
      const lastSyncTimestampRef = targetView === 'global' ? globalLastSyncTimestampRef : filteredLastSyncTimestampRef;
      const currentBucketSecondsRef = targetView === 'global' ? globalCurrentBucketSecondsRef : filteredCurrentBucketSecondsRef;
      
      // Use delta endpoint if we have previous sync timestamp (stored in ref to avoid stale closure)
      if (lastSyncTimestampRef.current) {
        // Use dashboard override if set, otherwise use settings, otherwise calculate from window
        let bucketSeconds: number;
        if (dashboardBucketOverrideRef.current !== null) {
          bucketSeconds = dashboardBucketOverrideRef.current;
        } else if (settingsRef.current?.bucket_seconds) {
          bucketSeconds = settingsRef.current.bucket_seconds;
        } else {
          // Fallback: calculate from connections_window
          const connectionsWindow = settingsRef.current?.connections_window || 5;
          if (connectionsWindow < 10) {
            bucketSeconds = 30;
          } else if (connectionsWindow < 60) {
            bucketSeconds = 60;
          } else {
            bucketSeconds = 300;
          }
        }
        
        // If bucket changed, force full refresh
        if (currentBucketSecondsRef.current !== null && currentBucketSecondsRef.current !== bucketSeconds) {
          if (targetView === 'global') {
            globalLastSyncTimestampRef.current = null;
          } else {
            filteredLastSyncTimestampRef.current = null;
          }
          dispatch({ type: 'SET_BUCKET_SECONDS', payload: { bucket: bucketSeconds, view: targetView } });
          // Recursively call with force=true to do full refresh
          return loadDashboardStats(true);
        }
        
        dispatch({ type: 'SET_BUCKET_SECONDS', payload: { bucket: bucketSeconds, view: targetView } });
        
        // Capture bucket size for this request to detect stale responses
        const requestBucketSeconds = bucketSeconds;
        
        // Incremental update
        const params = new URLSearchParams({
          since: lastSyncTimestampRef.current.toString(),
          bucket_seconds: bucketSeconds.toString(),
        });
        if (currentFilters?.channelId) params.append('channel_id', currentFilters.channelId);
        if (currentFilters?.gatewayId) params.append('gateway_id', currentFilters.gatewayId);
        if (currentFilters?.identityDid) params.append('identity_did', currentFilters.identityDid);
        if (currentFilters?.pipe_id) params.append('pipe_id', currentFilters.pipe_id);
        
        const url = `/api/v1/dashboard/delta?${params}`;
        
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const delta = await response.json();
        
        // Check if bucket size changed while request was in flight - ignore stale response
        if (currentBucketSecondsRef.current !== requestBucketSeconds) {
          // Bucket changed - this response is stale, ignore it and trigger fresh request
          return loadDashboardStats(true);
        }
        
        dispatch({ type: 'APPLY_DASHBOARD_DELTA', payload: { delta, view: targetView } });
      } else {
        // Full sync (first load or after error)
        
        // Use dashboard override if set, otherwise use settings, otherwise calculate from window
        let bucketSeconds: number;
        if (dashboardBucketOverrideRef.current !== null) {
          bucketSeconds = dashboardBucketOverrideRef.current;
        } else if (settingsRef.current?.bucket_seconds) {
          bucketSeconds = settingsRef.current.bucket_seconds;
        } else {
          // Fallback: calculate from connections_window
          const connectionsWindow = settingsRef.current?.connections_window || 5;
          if (connectionsWindow < 10) {
            bucketSeconds = 30;
          } else if (connectionsWindow < 60) {
            bucketSeconds = 60;
          } else {
            bucketSeconds = 300;
          }
        }
        
        dispatch({ type: 'SET_BUCKET_SECONDS', payload: { bucket: bucketSeconds, view: targetView } });
        
        // Capture bucket size for this request to detect stale responses
        const requestBucketSeconds = bucketSeconds;
        
        const stats = await apiClient.getDashboardStats(
          bucketSeconds,
          currentFilters?.channelId,
          currentFilters?.gatewayId,
          currentFilters?.identityDid,
          currentFilters?.pipe_id
        );
        
        // Check if bucket size changed while request was in flight - ignore stale response
        if (currentBucketSecondsRef.current !== requestBucketSeconds) {
          // Bucket changed - this response is stale, ignore it and trigger fresh request
          return loadDashboardStats(true);
        }
        
        dispatch({ type: 'SET_DASHBOARD_STATS', payload: { stats, view: targetView } });
      }
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to load dashboard stats' });
      
      // On error, reset sync state for the active view to force full stats next time
      const currentFilters = dashboardFiltersRef.current;
      const isFilteredView = currentFilters !== null && (
        currentFilters.channelId !== undefined ||
        currentFilters.gatewayId !== undefined ||
        currentFilters.identityDid !== undefined
      );
      if (isFilteredView) {
        filteredLastSyncTimestampRef.current = null;
      } else {
        globalLastSyncTimestampRef.current = null;
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'dashboard', value: false } });
    }
  }, []); // No dependencies - using refs to avoid stale closures

  // WebSocket connection management
  const wsRef = React.useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const disconnectTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const fullDisconnectTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const intentionalDisconnectRef = React.useRef<boolean>(false); // Flag to prevent auto-reconnect on intentional disconnect
  
  const connectWebSocket = React.useCallback(() => {
    if (wsRef.current) {
      return; // Already connected
    }

    // Don't connect websocket if not authenticated (no token or on sign-in page)
    const token = sessionStorage.getItem('session_token');
    const isOnSignInPage = window.location.pathname === '/login';
    
    if (!token || isOnSignInPage) {
      return;
    }

    // Clear intentional disconnect flag when connecting
    intentionalDisconnectRef.current = false;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let wsUrl = `${protocol}//${window.location.host}/api/ws`;
    wsUrl = `${wsUrl}?token=${encodeURIComponent(token)}`;
    
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      // Clear any pending disconnect timeouts when reconnecting
      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
        disconnectTimeoutRef.current = null;
      }
      if (fullDisconnectTimeoutRef.current) {
        clearTimeout(fullDisconnectTimeoutRef.current);
        fullDisconnectTimeoutRef.current = null;
      }
      
      dispatch({ type: 'SET_WS_STATUS', payload: 'CONNECTED' });
      dispatch({ type: 'SET_WS_CONNECTION', payload: ws });
      wsRef.current = ws;
    };

    ws.onmessage = (event) => {
      try {
        // Centralized JSON parsing - parse once and distribute to all listeners
        // This eliminates redundant parsing in individual components
        const message = JSON.parse(event.data);
        
        // Check for authentication errors
        if (message.type === 'error' && (message.error === 'unauthorized' || message.error === 'authentication_failed' || message.message?.includes('authentication'))) {
          console.error('WebSocket authentication failed - redirecting to sign in');
          // Close websocket
          disconnectWebSocket();
          // Clear session
          sessionStorage.removeItem('session_token');
          // Reload page to show login
          window.location.reload();
          return;
        }
        
        // Dispatch custom event with pre-parsed message for components
        // This allows components to receive parsed objects instead of re-parsing
        const customEvent = new CustomEvent('ws-message', { detail: message });
        window.dispatchEvent(customEvent);
        
        switch (message.type) {
          case 'connected':
            break;
          case 'identity_created':
            loadDashboardStats();
            break;
          case 'metrics_updated':
            loadDashboardStats();
            break;
          case 'log_entry':
            // Empty log entries are just pings to trigger refresh check
            // The throttling/scheduling mechanism will handle batching multiple events
            loadDashboardStats();
            break;
          case 'payload_captured':
            // Payload capture events are handled directly by the ChannelCapturePage component
            // No need to update global state here
            break;
          case 'refresh_dashboard':
            loadDashboardStats(true); // Force refresh, bypassing throttle
            break;
          default:
            // Unknown message type - silently ignore
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onclose = (event) => {
      dispatch({ type: 'SET_WS_CONNECTION', payload: null });
      wsRef.current = null;
      
      // Check if close was due to authentication failure (code 1008 = policy violation, often used for auth failures)
      // or code 4401 (custom unauthorized code some servers use)
      if (event.code === 1008 || event.code === 4401 || event.code === 1002) {
        console.error('WebSocket closed due to authentication failure - redirecting to sign in');
        // Set intentional disconnect to prevent reconnection
        intentionalDisconnectRef.current = true;
        // Clear session
        sessionStorage.removeItem('session_token');
        // Reload page to show login
        window.location.reload();
        return;
      }
      
      // If this was an intentional disconnect, don't try to reconnect
      if (intentionalDisconnectRef.current) {
        dispatch({ type: 'SET_WS_STATUS', payload: 'DISCONNECTED' });
        return;
      }
      
      // Before attempting reconnect, verify session is still valid
      const token = sessionStorage.getItem('session_token');
      if (!token) {
        dispatch({ type: 'SET_WS_STATUS', payload: 'DISCONNECTED' });
        window.location.reload();
        return;
      }
      
      // Check if session is still valid with the server (async operation)
      fetch(`/api/auth/check?session_token=${token}`)
        .then(response => {
          if (response.status === 404) {
            // Auth endpoint doesn't exist - passkey auth is disabled, allow reconnect
            return { authenticated: true };
          }
          return response.json();
        })
        .then(data => {
          if (!data.authenticated) {
            // Session is no longer valid - redirect to sign in
            sessionStorage.removeItem('session_token');
            window.location.reload();
            return;
          }
          // Session is valid, proceed with reconnection logic
          scheduleReconnect();
        })
        .catch(error => {
          // If we can't reach the server to check auth, it might be restarting
          // Redirect to sign in to be safe
          sessionStorage.removeItem('session_token');
          window.location.reload();
        });
    };
    
    // Helper function to schedule reconnection
    const scheduleReconnect = () => {
      // Keep showing "CONNECTED" initially, only change status if disconnection lasts > 3 seconds
      
      // Set timeout to show connecting if disconnection lasts too long
      disconnectTimeoutRef.current = setTimeout(() => {
        // Only change status if still disconnected
        if (!wsRef.current) {
          dispatch({ type: 'SET_WS_STATUS', payload: 'CONNECTING' });
          
          // Set another timeout to eventually show disconnected if still not reconnected
          fullDisconnectTimeoutRef.current = setTimeout(() => {
            if (!wsRef.current) {
              dispatch({ type: 'SET_WS_STATUS', payload: 'DISCONNECTED' });
            }
            fullDisconnectTimeoutRef.current = null;
          }, 7000); // Show disconnected after 10 seconds total (3 + 7)
        }
        disconnectTimeoutRef.current = null;
      }, 3000);
      
      // Attempt to reconnect after 1 second (faster reconnection for channel updates)
      reconnectTimeoutRef.current = setTimeout(() => {
        if (!wsRef.current) {
          connectWebSocket();
        }
        reconnectTimeoutRef.current = null;
      }, 1000);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      console.error('WebSocket readyState:', ws.readyState);
      dispatch({ type: 'SET_WS_STATUS', payload: 'ERROR' });
    };
  }, [loadDashboardStats]);

  const disconnectWebSocket = React.useCallback(() => {
    // Set flag to prevent automatic reconnection
    intentionalDisconnectRef.current = true;
    
    // Clear any pending reconnection timeouts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (disconnectTimeoutRef.current) {
      clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }
    if (fullDisconnectTimeoutRef.current) {
      clearTimeout(fullDisconnectTimeoutRef.current);
      fullDisconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      dispatch({ type: 'SET_WS_CONNECTION', payload: null });
      dispatch({ type: 'SET_WS_STATUS', payload: 'DISCONNECTED' });
      wsRef.current = null;
    }
  }, []);

  // Actions
  const actions = {
    toggleTheme: () => {
      const newTheme = state.theme === 'light' ? 'dark' : 'light';
      dispatch({ type: 'SET_THEME', payload: newTheme });
      localStorage.setItem('dashboard-theme', newTheme);
      
      // Apply theme to body
      if (newTheme === 'dark') {
        document.body.classList.add('dark-theme');
      } else {
        document.body.classList.remove('dark-theme');
      }
    },

    toggleSidebar: () => {
      dispatch({ type: 'TOGGLE_SIDEBAR' });
    },

    loadDashboardStats,
    
    setDashboardFilters: (filters: { channelId?: string; gatewayId?: string; identityDid?: string; pipe_id?: string } | null) => {
      // Update ref immediately BEFORE dispatch so it's available for loadDashboardStats
      dashboardFiltersRef.current = filters;
      
      // Dispatch to update state
      dispatch({ type: 'SET_DASHBOARD_FILTERS', payload: filters });
    },
    
    setActiveView: (view: 'global' | 'filtered') => {
      dispatch({ type: 'SET_ACTIVE_VIEW', payload: view });
    },

    loadSettings: async () => {
      dispatch({ type: 'SET_LOADING', payload: { key: 'settings', value: true } });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      try {
        const settings = await apiClient.getSettings();
        dispatch({ type: 'SET_SETTINGS', payload: settings });
      } catch (error) {
        console.error('[APPCONTEXT] Failed to load settings:', error);
        dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to load settings' });
        // Set default settings to prevent app crash
        dispatch({ type: 'SET_SETTINGS', payload: {
          badge_threshold: 5,
          metrics_retention: 6,
          task_activity_window: 60,
          connections_window: 60,
          latency_window: 60,
          onboarding_channel_ttl_seconds: 30,
          refresh_interval_seconds: 5,
          log_timestamp_format: 'local',
          bucket_seconds: 30,
        } });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: { key: 'settings', value: false } });
      }
    },

    updateSettings: async (settings: Partial<Settings>) => {
      try {
        await apiClient.updateSettings(settings);
        if (state.settings) {
          dispatch({ type: 'SET_SETTINGS', payload: { ...state.settings, ...settings } });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to update settings' });
        throw error;
      }
    },

    resetSettings: async () => {
      try {
        await apiClient.resetSettings();
        await actions.loadSettings(); // Reload settings after reset
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to reset settings' });
        throw error;
      }
    },

    truncateMetrics: async () => {
      try {
        const result = await apiClient.truncateMetrics();
        // Optionally reload dashboard stats to reflect changes (force immediate refresh)
        await loadDashboardStats(true);
        return result;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to truncate metrics' });
        throw error;
      }
    },

    reloadChannels: async () => {
      try {
        await apiClient.reloadAllChannels();
        // Reload dashboard stats to get updated channel information (force immediate refresh)
        await loadDashboardStats(true);
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to reload channels' });
        throw error;
      }
    },

    removeChannel: (configId: string) => {
      dispatch({ type: 'REMOVE_CHANNEL', payload: { configId } });
    },

    addChannel: (channel: any) => {
      dispatch({ type: 'ADD_CHANNEL', payload: { channel } });
    },

    updateChannel: (channel: any) => {
      dispatch({ type: 'UPDATE_CHANNEL', payload: { channel } });
    },

    setDashboardBucketOverride: (bucket: number | null) => {
      dispatch({ type: 'SET_DASHBOARD_BUCKET_OVERRIDE', payload: bucket });
    },

    connectWebSocket,
    disconnectWebSocket,
  };

  // Initialize data and WebSocket on mount
  useEffect(() => {
    // Load initial data
    const loadInitialData = async () => {
      // Load settings first (needed to determine bucket_seconds for dashboard stats)
      dispatch({ type: 'SET_LOADING', payload: { key: 'settings', value: true } });
      try {
        const settings = await apiClient.getSettings();
        settingsRef.current = settings; // Set ref immediately for loadDashboardStats
        dispatch({ type: 'SET_SETTINGS', payload: settings });
      } catch (error) {
        console.error('Failed to load settings:', error);
        dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to load settings' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: { key: 'settings', value: false } });
      }

      // Load dashboard stats (uses settingsRef.current.bucket_seconds)
      await loadDashboardStats();
    };

    loadInitialData();
    
    // Don't connect websocket on mount - it will be connected after authentication check in App.tsx
    // This prevents connection attempts before we know if the user is authenticated

    return () => {
      // Clean up timeouts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
      }
      if (fullDisconnectTimeoutRef.current) {
        clearTimeout(fullDisconnectTimeoutRef.current);
      }
      disconnectWebSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount to prevent WebSocket reconnection loop

  const contextValue: AppContextType = {
    state,
    dispatch,
    getCurrentStats: () => {
      return state.activeView === 'global' 
        ? state.globalState.stats 
        : state.filteredState.stats;
    },
    actions,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};